import { getParent, types, flow, getParentOfType } from 'mobx-state-tree';
import moment from 'moment';
import * as Papa from 'papaparse';
import ENUMS from '../../constants/enums';
import ExternalOrderData from '../../models/ExternalOrders/TableData';
import ExternalOrderV2Data from '../../models/ExternalOrders/ExternalOrderV2Data';
import UIStore from '../UIStore';
import ExtOrdersParams from './OrdersParams';
import ExtOrdersParamsV2 from './OrdersParamsV2';

const BASE_URL = 'autopurchase/v1/ExternalOrders';
const BASE_V2URL = 'autopurchase-v2-service/ExternalOrders';

const ExternalOrderStore = types
  .model('ExternalOrderStore', {
    ordersParams: types.optional(ExtOrdersParams, {}),
    ordersParamsV2: types.optional(ExtOrdersParamsV2, {}),
    ordersTableData: types.optional(types.array(ExternalOrderData), []),
    ordersTableV2Data: types.optional(types.array(ExternalOrderV2Data),[]),
    ordersSelected: types.optional(types.array(ExternalOrderData), []),
    totalOrders: 0,
    isSelectedAll: types.maybe(types.boolean, false),
  })
  .views(self => ({
    get showToast() {
      return getParentOfType(self, UIStore).toastsStore.showToast;
    },
    get accountCode() {
      return getParent(self).sessionStore.accountCode;
    },
    get transport5007() {
      return getParent(self).transport5007;
    },
    get bifmUserSource() {
      return getParent(self).sessionStore.bifmUserSource;
    },
  }))
  .actions(self => ({
    handleCheckboxClick(id) {
      const orderSelected = self.ordersTableData.find(order => order.externalOrderId === id);
      const bypass = { bypassMobx: 0 };
      const isAlreadySelected = self.ordersSelected.some(
        order => order.externalOrderId === orderSelected.externalOrderId,
      );
      isAlreadySelected
        ? (self.ordersSelected = self.ordersSelected.filter(
            item => item.externalOrderId !== orderSelected.externalOrderId,
          ))
        : self.ordersSelected.push({ ...orderSelected, bypass });
    },
    handleSelectPageOrders() {
      const bypass = { bypassMobx: 0 };
      self.ordersTableData.forEach(order => self.ordersSelected.push({ ...order, bypass }));
    },
    deselectAllOrders() {
      self.isSelectedAll = false;
      self.ordersSelected = [];
    },
    selectAllOrders: flow(function* selectAllOrders() {
      self.isSelectedAll = true;
      const externalSource = self.bifmUserSource;
      const params = {
        ...self.ordersParams,
        externalSource,
        limit: self.totalOrders,
        pageNumber: 1,
      };
      try {
        const {
          data: { data },
        } = yield self.transport5007.post(`${BASE_URL}/list`, params, {});
        self.ordersSelected = data;
        return true;
      } catch (err) {
        console.log('fetching orders data failed: ', err);
        return false;
      }
    }),
    // eslint-disable-next-line consistent-return
    getCsvOrdersData() {
      try {
        const jsonData = self.ordersSelected.map(
          ({
            externalOrderId,
            purchaseDate,
            sourcePrice,
            fee,
            totalCharge,
            buyerInfo,
            sourceItemId,
            sourceOrderId,
            carrier,
            orderStatus,
            quantity,
            trackingNumber,
            failureReason,
            expectedDelivery,
          }) => ({
            'Order ID': externalOrderId ?? 'N/A',
            Date: moment(purchaseDate).format('DD/MM/YYYY HH:mm') ?? 'N/A',
            Status: orderStatus ?? 'N/A',
            'Failure Reason': failureReason ?? 'N/A',
            ETA: expectedDelivery ?? 'N/A',
            'Source Price': sourcePrice ?? 'N/A',
            Fee: fee ?? 'N/A',
            'Total Charged': totalCharge ?? 'N/A',
            Quantity: quantity ?? 'N/A',
            Carrier: carrier ?? 'N/A',
            'Buyer Info': buyerInfo ?? 'N/A',
            'Source Item ID': sourceItemId ?? 'N/A',
            'Source Order ID': sourceOrderId ?? 'N/A',
            'Tracking Number': trackingNumber ?? 'N/A',
          }),
        );
        const csv = Papa.unparse(jsonData);
        return csv;
      } catch (error) {
        console.log('error: ', error);
      }
    },
    fetchOrders: flow(function* fetchOrders() {
      const externalSource = self.bifmUserSource;
      const params = { ...self.ordersParams, externalSource };
      try {
        const {
          data: { data, totalResults },
        } = yield self.transport5007.post(`${BASE_URL}/list`, params, {});
        self.ordersTableData = data;
        self.ordersParams.setTotal(totalResults);
        self.totalOrders = totalResults;
        return true;
      } catch (err) {
        console.log('fetching orders data failed: ', err);
        return false;
      }
    }),
    fetchV2Orders: flow(function* fetchOrders() {
      const externalSource = self.bifmUserSource;
      const params = { ...self.ordersParamsV2, externalSource };
      try {
        const {
          data: { data, totalResults },
        } = yield self.transport5007.post(`${BASE_V2URL}/list`, params, {});
        self.ordersTableV2Data = data;
        self.ordersParamsV2.setTotal(totalResults);
        self.totalOrders = totalResults;
        return true;
      } catch (err) {
        console.log('fetching orders data failed: ', err);
        return false;
      }
    }),
    tryAgain: flow(function* tryAgain(id) {
      try {
        yield self.transport5007.post(`${BASE_URL}/try-again/${id}`);
        self.showToast({
          type: ENUMS.TOAST_TYPES.INFO,
          message: 'The process of repurchasing the order has been started.',
        });
      } catch (err) {
        console.log(err);
        self.showToast({
          type: ENUMS.TOAST_TYPES.WARNING,
          message: 'An error has occurred. Please try again later.',
        });
      }
    }),
    tryAgainV2: flow(function* tryAgain(id) {
      try {
        yield self.transport5007.post(`${BASE_V2URL}/try-again/${id}`);
        self.showToast({
          type: ENUMS.TOAST_TYPES.INFO,
          message: 'The process of repurchasing the order has been started.',
        });
      } catch (err) {
        console.log(err);
        self.showToast({
          type: ENUMS.TOAST_TYPES.WARNING,
          message: 'An error has occurred. Please try again later.',
        });
      }
    }),
    forcePurchase: flow(function* forcePurchase(id) {
      try {
        yield self.transport5007.post(`${BASE_URL}/force-order/${id}`);
        self.showToast({
          type: ENUMS.TOAST_TYPES.INFO,
          message: 'The process of forcefully purchasing the order has been started.',
        });
      } catch (err) {
        console.log(err);
        self.showToast({
          type: ENUMS.TOAST_TYPES.WARNING,
          message: 'An error has occurred. Please try again later.',
        });
      }
    }),
    forcePurchaseV2: flow(function* forcePurchase(id) {
      try {
        yield self.transport5007.post(`${BASE_V2URL}/force-order/${id}`);
        self.showToast({
          type: ENUMS.TOAST_TYPES.INFO,
          message: 'The process of forcefully purchasing the order has been started.',
        });
      } catch (err) {
        console.log(err);
        self.showToast({
          type: ENUMS.TOAST_TYPES.WARNING,
          message: 'An error has occurred. Please try again later.',
        });
      }
    }),
    cancelOrder: flow(function* cancelOrder(id) {
      try {
        yield self.transport5007.post(`${BASE_URL}/cancel/${id}`);
        self.showToast({
          type: ENUMS.TOAST_TYPES.INFO,
          message: 'The process of cancelling the order has been started.',
        });
      } catch (err) {
        console.log(err);
        self.showToast({
          type: ENUMS.TOAST_TYPES.WARNING,
          message: 'An error has occurred. Please try again later.',
        });
      }
    }),
    cancelOrderV2: flow(function* cancelOrder(id) {
      try {
        yield self.transport5007.post(`${BASE_V2URL}/cancel/${id}`);
        self.showToast({
          type: ENUMS.TOAST_TYPES.INFO,
          message: 'The process of cancelling the order has been started.',
        });
      } catch (err) {
        console.log(err);
        self.showToast({
          type: ENUMS.TOAST_TYPES.WARNING,
          message: 'An error has occurred. Please try again later.',
        });
      }
    }),
    returnOrder: flow(function* returnOrder(id) {
      try {
        yield self.transport5007.post(`${BASE_URL}/return/${id}`);
        self.showToast({
          type: ENUMS.TOAST_TYPES.INFO,
          message: 'The process of returning the order has been started.',
        });
      } catch (err) {
        console.log(err);
        self.showToast({
          type: ENUMS.TOAST_TYPES.WARNING,
          message: 'An error has occurred. Please try again later.',
        });
      }
    }),
    returnOrderV2: flow(function* returnOrder(id) {
      try {
        yield self.transport5007.post(`${BASE_V2URL}/return/${id}`);
        self.showToast({
          type: ENUMS.TOAST_TYPES.INFO,
          message: 'The process of returning the order has been started.',
        });
      } catch (err) {
        console.log(err);
        self.showToast({
          type: ENUMS.TOAST_TYPES.WARNING,
          message: 'An error has occurred. Please try again later.',
        });
      }
    }),
  }));

export default ExternalOrderStore;
